import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { useAuthStore } from '@presentation/stores/authStore';
import { QUERY_KEYS } from '@shared/constants';
import { paths } from '@app/routes/paths';
import type {
  LoginCredentials,
  RegisterPayload,
  ResetPasswordPayload,
  TwoFactorChallengePayload,
} from '@core/repositories/AuthRepository';

export const useCurrentUser = () => {
  const setUser = useAuthStore((state) => state.setUser);

  return useQuery({
    queryKey: QUERY_KEYS.currentUser,
    queryFn: async () => {
      const user = await container.auth.getCurrentUser.execute();
      setUser(user);
      return user;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => container.auth.login.execute(credentials),
    onSuccess: (outcome) => {
      if (outcome.status === 'authenticated') {
        setUser(outcome.user);
        queryClient.setQueryData(QUERY_KEYS.currentUser, outcome.user);
      }
    },
  });
};

export const useTwoFactorChallenge = () => {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (payload: TwoFactorChallengePayload) =>
      container.auth.challengeTwoFactor.execute(payload),
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(QUERY_KEYS.currentUser, user);
    },
  });
};

export const useRegister = () => {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (payload: RegisterPayload) => container.auth.register.execute(payload),
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(QUERY_KEYS.currentUser, user);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const reset = useAuthStore((state) => state.reset);

  return useMutation({
    mutationFn: () => container.auth.logout.execute(),
    onSuccess: () => {
      reset();
      queryClient.removeQueries({ queryKey: QUERY_KEYS.currentUser });
      // A hard navigation (not client-side routing) so every in-memory
      // store/cache is wiped clean rather than relying on route guards to
      // catch up with the just-reset auth state.
      window.location.href = paths.login;
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: (email: string) => container.auth.forgotPassword.execute(email),
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (payload: ResetPasswordPayload) => container.auth.resetPassword.execute(payload),
  });
};

export const useResendVerificationEmail = () => {
  return useMutation({
    mutationFn: () => container.auth.resendVerificationEmail.execute(),
  });
};
